(ns manage.ad-variant-preview.widgets.video-preview
  (:require ["@mui/material/Skeleton$default" :as Skeleton]
            ["@mui/material/Stack$default" :as Stack]
            ["react-use/lib/useEvent$default" :as useEvent]
            [cljs-bean.core :refer [->clj ->js]]
            [manage.ad-variant-preview.data :as data]
            [manage.ad-variant-preview.viewport :as viewport]
            [manage.ad-variant-preview.widgets.component-error :refer [component-error]]
            [manage.ad-variant-preview.widgets.loading-spinner :refer [loading-spinner]]
            [uix.core :as uix :refer [$ defui]]))

;; Video terminology:
;; Playhead = video currentTime
;; Seek = moving the playhead, while dragging the position on timeline, playback is paused
;; Video selection = Our timeline selection tool with start&end timestamps
;; end status = means the video is either really at the end, or paused at or after the end selection timestamp,
;;              used to control when auto-replay triggers.

(defn use-intersection [ref opts]
  (let [[in-view? set-in-view] (uix/use-state false)]
    (uix/use-effect (fn []
                      (if (some? @ref)
                        (let [handler (fn [entries]
                                        (set-in-view (boolean (some #(.-isIntersecting %) (->clj entries)))))
                              observer (js/IntersectionObserver. handler (->js opts))]
                          (.observe observer @ref)
                          (fn []
                            (set-in-view false)
                            (.disconnect observer)))
                        js/undefined))
                    [opts ref])
    in-view?))

(defui video-player
  "This element is only rendered when the video player is in view,
  so the video-status & event handlers are here, so
  they are only added to the video elements that are in the view."
  [{:keys [variant component video-controls set-video-status
           width height]}]
  (let [{:keys [event-target video-status-ref]} (viewport/use-context)
        {:keys [uuid]} variant
        {:keys [value]} component
        {:keys [audio-active-variant start end]} video-controls

        video-ref (uix/use-ref nil)
        ;; Store state if playback was temporary paused when starting seek, so
        ;; we can restore play status when seek is done.
        temp-pause (uix/use-ref false)

        audio-active (= uuid audio-active-variant)]
    (uix/use-effect (fn []
                      ;; Also could consider keeping a ref value to the longest-video in the ctx.
                      (when-let [main-el (some-> video-status-ref
                                                 deref
                                                 data/find-longest-video
                                                 :video
                                                 :el)]
                        ;; NOTE: If video isn't fully loaded yet, seeking will take some time and the
                        ;; currentTime isn't accurate when this element is loaded and starts playing.
                        ;; The copy operation could be moved to loaded callback, or something.
                        #_(js/console.log "copy state from current main video element"
                                          #js {:t (.-currentTime main-el)
                                               :paused (.-paused main-el)})
                        (set! (.-currentTime @video-ref) (.-currentTime main-el))
                        (when-not (.-paused main-el)
                          (.play @video-ref)))
                      (set-video-status assoc :el @video-ref)
                      (fn []
                        (set-video-status nil)))
                    ;; set-video-status is stable
                    ^:lint/disable
                    [])

    ;; TODO: On seek, wait for all videos to be ready to play the seek position before continuing play?

    ;; Follow muted control
    (uix/use-effect (fn []
                      (when-let [el @video-ref]
                        (set! (.-muted el) (not audio-active))))
                    ^:lint/disable
                    [audio-active])

    ;; Subscribe to control events
    (useEvent "toggleMute"
              (fn [_e]
                (let [muted (.-muted @video-ref)]
                  (set! (.-muted @video-ref) (not muted))))
              event-target)

    (useEvent "play"
              (fn [e]
                (let [{:keys [all-end]} (.-data e)
                      t (.-currentTime @video-ref)]
                  ;; If outside the selection, go to the selection start.
                  ;; Takes care of going to the start if stopped at the end.
                  (when (or (and start (<= t start))
                            (and end (>= t end)))
                    (set! (.-currentTime @video-ref) start))
                  ;; Start playing if this video element isn't at the end already,
                  ;; unless told all videos are at the end.
                  (when (or all-end
                            (and end (< t end))
                            (not (.-ended @video-ref)))
                    (.play @video-ref))))
              event-target)

    (useEvent "pause"
              (fn [_e]
                (.pause @video-ref))
              event-target)

    (useEvent "seek"
              (fn [e]
                (let [{:keys [target save-position restore-position]} (.-data e)]
                  ;; Pause and store position while seeking
                  (when (and save-position (not (.-paused @video-ref)))
                    (reset! temp-pause true)
                    (.pause @video-ref))

                  (when target
                    (set! (.-currentTime @video-ref) target))

                  (when (and restore-position @temp-pause)
                    (reset! temp-pause false)
                    (.play @video-ref))))
              event-target)

    ($ :video
       {:src value
        :ref video-ref
        :autoPlay false
        ;; TODO: Does this work?
        :muted true
        :controls false
        :width width
        :height height
        :style {;; Drag and drop needs this, else clicks go to the
                ;; video controls.
                :pointer-events "none"}
        :type "video/mp4"

        :onPlaying
        (fn [_e]
          (set-video-status assoc :status "playing"))

        :onPause
        (fn [e]
          (set-video-status assoc :status (if (and end (>= (.-currentTime (.-target e)) end))
                                            "end"
                                            "paused")))

        :onLoadedMetadata
        (fn [e]
          (let [v (.. e -target -duration)]
            (set-video-status assoc :duration v)))

        ;; Playback status is read on bottom-panel using RAF callback.
        ;; This event is triggered only every ~0.4s.
        :onTimeUpdate (fn [e]
                        (when (and end (>= (.-currentTime (.-target e)) end))
                          (.pause (.-target e))
                          (set-video-status assoc :status "end")))

        :onEnded
        (fn [_e]
          (set-video-status assoc :status "end"))})))

(defui uncontrolled-video-player
  [{:keys [component width height]}]
  (let [{:keys [value]} component]
    ($ :video
       {:src value
        :autoPlay false
        :muted true
        :controls true
        :width width
        :height height
        :type "video/mp4"})))

(defui video-preview [{:keys [width height variant component set-video-status video-controls]}]
  (let [{:keys [pan-area-el-ref]} (viewport/use-context)
        {:keys [error pending]} component

        intersection-ref (uix/use-ref)
        in-view (use-intersection intersection-ref
                                  {;; This is only available on pan mode
                                   :root (when pan-area-el-ref @pan-area-el-ref)
                                   ;; Add quarter of the container size around the container, so
                                   ;; elements are rendered a bit before the become visible
                                   :rootMargin "25%"
                                   ;; If even a single px of the element is visible
                                   :threshold 0})]
    ($ Stack
       {:ref intersection-ref}
       (cond
         error
         ($ component-error
            {:width width
             :height height})

         pending
         ($ loading-spinner
            {:width width
             :height height})

         (not in-view)
         ($ Skeleton
            {:width width
             :height height})

         :else
         (if (and set-video-status video-controls)
           ($ video-player
              {:video-controls video-controls
               :set-video-status set-video-status
               :variant variant
               :component component
               :width width
               :height height})
           ($ uncontrolled-video-player
              {:component component
               :width width
               :height height}))))))
